import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";

import type { UserMenu } from "../dtos/get-profile-data-response-dto";
import { ProfileService } from "../services/profile-service";
import type { AuthUser } from "../types";
import { useAccessToken } from "./use-access-token";
import { useUserData } from "./use-user-data";

type LoginParams = {
  accessToken: string;
  userData: AuthUser;
};

export const useLogin = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [, setAccessToken] = useAccessToken();
  const [, setUserData] = useUserData();

  return async (params: LoginParams): Promise<boolean> => {
    if (!params.accessToken || !params.userData) {
      throw new Error("Invalid login params");
    }

    await setAccessToken(params.accessToken);
    await setUserData(params.userData);

    // Fetch profile data to get user menus before navigating
    let userMenus: UserMenu[] = [];
    let userData = params.userData;

    try {
      const profileData = await queryClient.fetchQuery({
        queryKey: ["profile"],
        queryFn: () => ProfileService.getProfileData()
      });

      userMenus = profileData?.data?.menus || [];

      const sellerType = profileData?.data?.seller_type;
      if (sellerType) {
        userData = { ...params.userData, seller_type: sellerType };
        await setUserData(userData);
      }
    } catch {
      // If profile fetch fails, use empty menus
      // User will still be authenticated but may have limited access
    }

    // Update router context BEFORE navigating to ensure auth state is set
    await router.update({
      context: {
        auth: {
          isAuthenticated: true,
          userData,
          userMenus
        }
      }
    });

    return true;
  };
};
